import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, CheckCircle2, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, money, fmtDate, fmtKST } from "@/lib/api";
import { StatusBadge } from "@/components/Bits";
import { CopyHandle } from "@/components/PaymentForm";

const StatusLine = ({ j }) => {
  if (j.ems_status === "paid") return <div data-testid="higher-ems-status" className="flex items-center gap-2 text-emerald-300 font-semibold"><CheckCircle2 className="h-5 w-5" /> EMS Paid</div>;
  if (j.ems_status === "pending") return <div data-testid="higher-ems-status" className="flex items-center gap-2 text-amber-300 font-semibold"><Clock className="h-5 w-5" /> Payment under review · {money(j.ems_pending)}</div>;
  return <div data-testid="higher-ems-status" className="font-semibold text-pink-300">EMS left to pay · <span className="font-mono">{money(j.ems_due)}</span></div>;
};

const DueLine = ({ j }) => {
  if (!j.pay_by || j.ems_status === "paid" || j.ems_status === "none") return null;
  const tone = j.past_due ? "border-red-500/40 bg-red-500/10 text-red-300" : j.due_soon ? "border-amber-400/40 bg-amber-400/10 text-amber-200" : "border-zinc-700 text-zinc-300";
  return (
    <div data-testid="higher-ems-payby" className={`mt-2 inline-flex flex-wrap items-center gap-2 rounded-full border px-3 py-1 text-xs ${tone}`}>
      {j.past_due && <span data-testid="higher-ems-past-due-tag" className="font-bold">Past due</span>}
      {j.due_soon && <span data-testid="higher-ems-due-soon-tag" className="font-bold">Due soon</span>}
      <span>Pay by {fmtKST(j.pay_by)}</span>
    </div>
  );
};

const History = ({ list }) => (
  <div className="mt-4 space-y-1.5" data-testid="higher-ems-history">
    {list.map((p) => (
      <div key={p.id} data-testid={`higher-ems-payment-${p.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-white/5 bg-zinc-950/60 px-3 py-2 text-sm">
        <span className="font-mono">{money(p.amount)}</span>
        <StatusBadge status={p.status} />
        <span className="text-xs text-zinc-500">{fmtDate(p.created_at)}</span>
        {p.admin_note && <span className="basis-full text-xs text-zinc-400">Admin note: {p.admin_note}</span>}
      </div>
    ))}
  </div>
);

const PayForm = ({ j, onDone }) => {
  const [methods, setMethods] = useState([]);
  const [reference, setReference] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const toggle = (m) => setMethods((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));
  const submit = async (e) => {
    e.preventDefault();
    if (!methods.length) return toast.error("Tick the payment method you used");
    if (!file) return toast.error("Attach your payment screenshot");
    const fd = new FormData();
    fd.append("joiner_id", j.id);
    fd.append("reference", reference);
    methods.forEach((m) => fd.append("methods", m));
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/higher/payments", fd);
      toast.success("EMS payment submitted — waiting for admin review");
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't submit payment"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="higher-ems-pay-form" className="mt-4 grid sm:grid-cols-[1fr_220px] gap-4">
      <div className="space-y-3 min-w-0">
        <div className="space-y-2 rounded-xl border border-zinc-800 p-3">
          <Label>Send {money(j.ems_due)} to {j.pay_to} — tick the method you used</Label>
          <p data-testid="higher-ems-pay-instructions" className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-100">Please leave the notes portion blank, if you need to add a note please put your username minus any words such as trades, sales, etc.</p>
          {j.pay_methods.map((m, i) => (
            <div key={m} className="flex items-center gap-3 text-sm">
              <label className="flex flex-1 items-center gap-3 cursor-pointer min-w-0">
                <Checkbox data-testid={`higher-ems-method-${i}`} checked={methods.includes(m)} onCheckedChange={() => toggle(m)} />
                <span className="break-all">{m}</span>
              </label>
              <CopyHandle handle={m.split(": ")[1]} testId={`higher-ems-method-copy-${i}`} />
            </div>
          ))}
        </div>
        <Input data-testid="higher-ems-reference-input" placeholder="Reference / transaction ID (optional)" value={reference} onChange={(e) => setReference(e.target.value)} className="bg-zinc-950" />
        <Button type="submit" disabled={busy} data-testid="higher-ems-submit" className="h-11 px-6 rounded-xl bg-gradient-to-r from-sky-600 to-violet-600 hover:opacity-90 text-white font-semibold">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit EMS payment
        </Button>
      </div>
      <label className="order-first sm:order-none relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-zinc-700 hover:border-sky-400/60 transition-colors cursor-pointer min-h-[180px] overflow-hidden bg-zinc-950">
        <input type="file" accept="image/*" className="hidden" data-testid="higher-ems-screenshot-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {preview ? <img src={preview} alt="Payment screenshot preview" data-testid="higher-ems-screenshot-preview" className="absolute inset-0 h-full w-full object-contain" />
          : <div className="text-center p-4"><ImagePlus className="h-7 w-7 mx-auto text-zinc-500" /><div className="mt-2 text-sm font-medium">Upload screenshot</div><div className="text-xs text-zinc-500 mt-1">up to 8 MB</div></div>}
      </label>
    </form>
  );
};

export default function HigherEmsPayment({ j, onChanged }) {
  if (j.ems_status === "none" && !j.payments.length) return null;
  return (
    <section data-testid="higher-ems-payment" className="mt-8 rounded-2xl border border-white/10 bg-zinc-900/60 p-4 sm:p-5">
      <h2 className="font-display text-base md:text-lg font-bold">EMS Payment</h2>
      <p className="text-xs text-zinc-500 mt-0.5">One payment covers all your Higher EMS. It shows as Paid once an admin approves your proof.</p>
      <div className="mt-3"><StatusLine j={j} /><DueLine j={j} /></div>
      {j.ems_due > 0 && <PayForm j={j} onDone={onChanged} />}
      {j.payments.length > 0 && <History list={j.payments} />}
    </section>
  );
}
